import { createSlice } from '@reduxjs/toolkit';
import { firstValueFrom } from 'rxjs';
import passengerService from '../services/passengerService';
import reservationService from '../services/reservationService';
import { PagedList } from './models/paged-list.model';
import {
  Passenger,
  PassengerAddress,
  PassengerPreference,
} from './models/passenger.model';
import { Reservation, TripLeg } from './models/reservation.model';
import * as uiSlice from './ui.slice';
import {
  TransportReservationF1FormattedResponse,
  TransportReservationRequest,
  TransportReservationResponse,
} from '@/data/models/transport-reservation.model';

type NextPageData = {
  hasMorePages: boolean;
  pageSize: number;
  nextPage?: number | null;
};

interface PassengerState {
  passengerSearchClientBC: string;
  passengerSearchText: string;
  passengerSearchPage: number;
  passengerSearchPageSize: number;
  passengers: PagedList<Passenger>;
  loadingPassengers: boolean;
  loadedPassengers: boolean;
  currentPassenger: Passenger | null;
  loadingCurrentPassenger: boolean;
  loadedCurrentPassenger: boolean;
  currentPassengerPreferences: PassengerPreference[];
  loadingCurrentPassengerPreferences: boolean;
  loadedCurrentPassengerPreferences: boolean;
  savingCurrentPassengerPreferences: boolean;
  savedCurrentPassengerPreferences: boolean;
  currentPassengerPastReservations: Reservation[];
  currentPassengerPastReservationsNextPageData?: NextPageData;
  loadingCurrentPassengerPastReservations: boolean;
  loadedCurrentPassengerPastReservations: boolean;
  currentPassengerFutureReservations: Reservation[];
  // Standing orders cancelled optimistically whose backend cascade has not yet
  // completed. While an entry is pending, refetched future pages suppress that
  // STO's occurrences so a mid-cascade refetch cannot resurrect rows the user
  // just cancelled. Cleared by server confirmation (a full page-1 response
  // with zero occurrences) or a passenger switch — deliberately NOT by time:
  // if the backend permanently fails to cancel some occurrences they stay
  // hidden here until refresh/switch (accepted tradeoff). See
  // docs/sto-list-refresh-plan.md.
  pendingStandingOrderCancels: Record<string, true>;
  currentPassengerFutureReservationsNextPageData?: NextPageData;
  loadingCurrentPassengerFutureReservations: boolean;
  loadedCurrentPassengerFutureReservations: boolean;
  currentPassengerReservationLookup: Reservation | null;
  loadingCurrentPassengerReservationLookup: boolean;
  loadedCurrentPassengerReservationLookup: boolean;
  savingPassenger: boolean;
  savedPassenger: boolean;

  passengerEventEmitted: Passenger | null;
}

export const initialState: PassengerState = {
  passengerSearchClientBC: '',
  passengerSearchText: '',
  passengerSearchPage: 1,
  passengerSearchPageSize: 20,
  passengers: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  loadingPassengers: false,
  loadedPassengers: false,
  currentPassenger: null,
  loadingCurrentPassenger: false,
  loadedCurrentPassenger: false,
  currentPassengerPreferences: [],
  loadingCurrentPassengerPreferences: false,
  loadedCurrentPassengerPreferences: false,
  savingCurrentPassengerPreferences: false,
  savedCurrentPassengerPreferences: false,
  currentPassengerPastReservations: [],
  currentPassengerPastReservationsNextPageData: undefined,
  loadingCurrentPassengerPastReservations: false,
  loadedCurrentPassengerPastReservations: false,
  currentPassengerFutureReservations: [],
  pendingStandingOrderCancels: {},
  currentPassengerFutureReservationsNextPageData: undefined,
  loadingCurrentPassengerFutureReservations: false,
  loadedCurrentPassengerFutureReservations: false,
  currentPassengerReservationLookup: null,
  loadingCurrentPassengerReservationLookup: false,
  loadedCurrentPassengerReservationLookup: false,
  savingPassenger: false,
  savedPassenger: false,

  passengerEventEmitted: null,
};

const tripLegStatusCanceled = 'canceled';
const tripLegStatusCanceledLate = 'canceledlate';
const tripLegStatusBCCanceled = 'CANCELED';
const tripLegStatusBCCanceledLate = 'CANCELEDLATE';

const canceledTripLegStatuses = new Set<string>([
  tripLegStatusCanceled,
  tripLegStatusCanceledLate,
]);

const isCanceledTripLeg = (tripLeg: TripLeg) =>
  canceledTripLegStatuses.has(tripLeg.status?.toLowerCase() || '');

const sortTripLegsBySequence = (tripLegs: TripLeg[]) =>
  [...tripLegs].sort(
    (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
  );

const filterMissingCanceledTripLegs = (
  incomingTripLegs: TripLeg[],
  existingTripLegs: TripLeg[],
) => {
  const incomingTripLegIDs = new Set(
    incomingTripLegs.map((tripLeg) => tripLeg.tripLegReferenceID),
  );

  return existingTripLegs.filter(
    (tripLeg) =>
      !incomingTripLegIDs.has(tripLeg.tripLegReferenceID) &&
      isCanceledTripLeg(tripLeg),
  );
};

const preserveExistingCanceledTripLegs = (
  incomingReservations: Reservation[],
  existingReservations: Reservation[],
) =>
  incomingReservations.map((incomingReservation) => {
    const existingReservation = existingReservations.find(
      (reservation) =>
        reservation.reservationReferenceID ===
          incomingReservation.reservationReferenceID &&
        reservation.passengerUUID === incomingReservation.passengerUUID,
    );
    if (!existingReservation) return incomingReservation;

    const missingCanceledTripLegs = filterMissingCanceledTripLegs(
      incomingReservation.tripLegs,
      existingReservation.tripLegs,
    );
    if (!missingCanceledTripLegs.length) return incomingReservation;

    return {
      ...incomingReservation,
      tripLegs: sortTripLegsBySequence([
        ...incomingReservation.tripLegs,
        ...missingCanceledTripLegs,
      ]),
    };
  });

const belongsToStandingOrder = (
  reservation: Reservation,
  standingOrderUUID: string,
) => reservation.standingOrderUUID === standingOrderUUID;

/**
 * Single definition of "does this response have pages beyond it", shared by
 * the pagination bookkeeping (getNextPageData) and the standing-order cancel
 * confirmation so the two can never disagree. Missing fields and the API
 * family's currentPage/totalPages 0-on-empty shape (see setPassengers'
 * normalization) both count as "no more pages".
 */
const responseHasMorePages = (
  response: TransportReservationF1FormattedResponse,
) => (response.totalPages ?? 1) > (response.currentPage ?? 1);

/**
 * Clears pending standing-order cancels the server has confirmed finished: a
 * COMPLETE future response (page 1 with no further pages) containing zero
 * occurrences of a pending STO proves the backend's cancel cascade got them
 * all — any occurrence it genuinely failed to cancel resurfaces on the next
 * refetch after that. A multi-page response proves nothing (occurrences may
 * sort beyond page 1), so confirmation is skipped and the suppression simply
 * persists. There is deliberately no time-based expiry: a cascade that never
 * confirms keeps its occurrences hidden until a refresh or passenger switch
 * (accepted tradeoff — reappearing rows mid-session were judged worse than a
 * stale hide in the rare backend-failure case). See
 * docs/sto-list-refresh-plan.md.
 */
const confirmCompletedStandingOrderCancels = (
  state: PassengerState,
  response: TransportReservationF1FormattedResponse,
) => {
  const isCompleteView =
    !responseHasMorePages(response) && (response.currentPage ?? 1) <= 1;
  if (!isCompleteView) return;

  const returnedStandingOrderUUIDs = new Set(
    response.items
      .map((reservation) => reservation.standingOrderUUID)
      .filter(Boolean),
  );
  for (const uuid of Object.keys(state.pendingStandingOrderCancels)) {
    if (!returnedStandingOrderUUIDs.has(uuid)) {
      delete state.pendingStandingOrderCancels[uuid];
    }
  }
};

/**
 * Drops occurrences of optimistically-cancelled standing orders from a
 * refetched future page, so a refetch that races the backend's cancel cascade
 * cannot resurrect rows the user just removed. Pure filter — clearing the
 * suppression is confirmCompletedStandingOrderCancels' job.
 */
const dropPendingStandingOrderOccurrences = (
  incoming: Reservation[],
  pending: PassengerState['pendingStandingOrderCancels'],
) => {
  if (!Object.keys(pending).length) return incoming;
  return incoming.filter((reservation) => {
    const uuid = reservation.standingOrderUUID;
    return !uuid || pending[uuid] !== true;
  });
};

const getNextPageData = (response: TransportReservationF1FormattedResponse) => {
  const hasMorePages = responseHasMorePages(response);
  const currentPage = response.currentPage ?? 1;

  return {
    hasMorePages,
    nextPage: hasMorePages ? currentPage + 1 : null,
    pageSize: response.filters.pageSize!,
  };
};

export const slice = createSlice({
  name: 'passenger',
  initialState,
  reducers: {
    setPassengerSearchClientBC: (
      state: PassengerState,
      action: { type: string; payload: string },
    ) => {
      state.passengerSearchClientBC = action.payload;
    },
    setPassengerSearchText: (
      state: PassengerState,
      action: { type: string; payload: string },
    ) => {
      state.passengerSearchText = action.payload;
    },
    setPassengerSearchPage: (
      state: PassengerState,
      action: { type: string; payload: number },
    ) => {
      state.passengerSearchPage = action.payload;
    },
    setPassengerSearchPageSize: (
      state: PassengerState,
      action: { type: string; payload: number },
    ) => {
      state.passengerSearchPageSize = action.payload;
    },
    setPassengers: (
      state: PassengerState,
      action: { type: string; payload: PagedList<Passenger> },
    ) => {
      const list = action.payload;
      if (list.currentPage === 0) {
        list.currentPage = 1;
      }
      state.passengers = list;
    },
    clearPassengers: (state: PassengerState, action: { type: string }) => {
      state.passengers = {
        itemCount: 0,
        currentPage: 1,
        totalPages: 0,
        items: [],
      };
      state.loadingPassengers = false;
      state.loadedPassengers = false;
      state.passengerSearchPage = 1;
    },
    setLoadingPassengers: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPassengers = action.payload;
    },
    setLoadedPassengers: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPassengers = action.payload;
    },
    setCurrentPassenger: (
      state: PassengerState,
      action: { type: string; payload: Passenger | null },
    ) => {
      state.currentPassenger = action.payload;
    },
    setLoadingCurrentPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassenger = action.payload;
    },
    setLoadedCurrentPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassenger = action.payload;
    },
    setCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: PassengerPreference[] },
    ) => {
      state.currentPassengerPreferences = action.payload;
    },
    setLoadingCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerPreferences = action.payload;
    },
    setLoadedCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerPreferences = action.payload;
    },
    setSavingCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingCurrentPassengerPreferences = action.payload;
    },
    setSavedCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedCurrentPassengerPreferences = action.payload;
    },
    setCurrentPassengerPastReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      state.currentPassengerPastReservations = action.payload.items;
    },
    appendCurrentPassengerPastReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      state.currentPassengerPastReservations = [
        ...state.currentPassengerPastReservations,
        ...action.payload.items,
      ];
    },
    setLoadingCurrentPassengerPastReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerPastReservations = action.payload;
    },
    setLoadedCurrentPassengerPastReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerPastReservations = action.payload;
    },
    setCurrentPassengerPastReservationsNextPageData: (
      state,
      action: { payload: TransportReservationF1FormattedResponse },
    ) => {
      state.currentPassengerPastReservationsNextPageData = getNextPageData(
        action.payload,
      );
    },
    setCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      confirmCompletedStandingOrderCancels(state, action.payload);
      state.currentPassengerFutureReservations =
        preserveExistingCanceledTripLegs(
          dropPendingStandingOrderOccurrences(
            action.payload.items,
            state.pendingStandingOrderCancels,
          ),
          state.currentPassengerFutureReservations,
        );
    },
    appendCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      state.currentPassengerFutureReservations = [
        ...state.currentPassengerFutureReservations,
        // A later page can't prove an STO's absence, so it only filters and
        // never confirms/clears a pending cancel.
        ...dropPendingStandingOrderOccurrences(
          action.payload.items,
          state.pendingStandingOrderCancels,
        ),
      ];
    },
    setLoadingCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerFutureReservations = action.payload;
    },
    setLoadedCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerFutureReservations = action.payload;
    },
    setCurrentPassengerFutureReservationsNextPageData: (
      state,
      action: { payload: TransportReservationF1FormattedResponse },
    ) => {
      state.currentPassengerFutureReservationsNextPageData = getNextPageData(
        action.payload,
      );
    },
    setCurrentPassengerReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: Reservation | null },
    ) => {
      state.currentPassengerReservationLookup = action.payload;
    },
    setLoadingCurrentPassengerReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerReservationLookup = action.payload;
    },
    setLoadedCurrentPassengerReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerReservationLookup = action.payload;
    },
    clearCurrentPassengerReservationLookup: (
      state: PassengerState,
      _action: { type: string },
    ) => {
      state.currentPassengerReservationLookup = null;
      state.loadingCurrentPassengerReservationLookup = false;
      state.loadedCurrentPassengerReservationLookup = false;
    },
    mergeTripLegIntoReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: TripLeg },
    ) => {
      if (!state.currentPassengerReservationLookup) return;
      const existing = state.currentPassengerReservationLookup.tripLegs;
      const alreadyPresent = existing.some(
        (l) => l.tripLegReferenceID === action.payload.tripLegReferenceID,
      );
      if (alreadyPresent) return;
      const merged = [...existing, action.payload].sort(
        (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
      );
      state.currentPassengerReservationLookup = {
        ...state.currentPassengerReservationLookup,
        tripLegs: merged,
      };
    },
    addFuturePassengerReservation: (
      state: PassengerState,
      action: { type: string; payload: Reservation },
    ) => {
      state.currentPassengerFutureReservations = [
        action.payload,
        ...state.currentPassengerFutureReservations,
      ];
    },
    addPastPassengerReservation: (
      state: PassengerState,
      action: { type: string; payload: Reservation },
    ) => {
      state.currentPassengerPastReservations = [
        action.payload,
        ...state.currentPassengerPastReservations,
      ];
    },
    addFutureTripLeg: (
      state: PassengerState,
      action: {
        type: string;
        payload: { reservationReferenceID: string; tripLeg: TripLeg };
      },
    ) => {
      const reservation = state.currentPassengerFutureReservations.find(
        (r) =>
          r.reservationReferenceID === action.payload.reservationReferenceID,
      );
      if (reservation) {
        reservation.tripLegs.push(action.payload.tripLeg);
      }
    },
    updateFutureTripLegs: (
      state: PassengerState,
      action: {
        type: string;
        payload: TripLeg[];
      },
    ) => {
      const reservation = state.currentPassengerFutureReservations.find(
        (r) =>
          r.reservationReferenceID === action.payload[0].reservationReferenceID,
      );
      if (reservation) {
        const oldLegs = [...reservation.tripLegs];
        const newLegs = action.payload;
        newLegs.forEach((newLeg) => {
          const idx = oldLegs.findIndex(
            (l) => l.tripLegReferenceID === newLeg.tripLegReferenceID,
          );
          if (idx >= 0) {
            oldLegs.splice(idx, 1, newLeg);
          }
        });
        reservation.tripLegs = oldLegs;
      }
    },
    replaceReservation: (
      state: PassengerState,
      action: {
        type: string;
        payload: Reservation;
      },
    ) => {
      const reservations = state.currentPassengerFutureReservations;
      const idx = reservations.findIndex(
        (r) =>
          r.reservationReferenceID === action.payload.reservationReferenceID,
      );
      if (idx >= 0) {
        reservations.splice(idx, 1, action.payload);
      }
    },
    replaceReservationAfterCancelTripLeg: (
      state: PassengerState,
      action: {
        type: string;
        payload: {
          reservation: Reservation;
          tripLegReferenceID: string;
        };
      },
    ) => {
      const reservations = state.currentPassengerFutureReservations;
      const idx = reservations.findIndex(
        (r) =>
          r.reservationReferenceID ===
          action.payload.reservation.reservationReferenceID,
      );
      if (idx < 0) return;

      const existingReservation = reservations[idx];
      const responseTripLegs = action.payload.reservation.tripLegs || [];
      const responseTripLegIDs = new Set(
        responseTripLegs.map((l) => l.tripLegReferenceID),
      );
      const missingExistingTripLegs = existingReservation.tripLegs.filter(
        (l) => !responseTripLegIDs.has(l.tripLegReferenceID),
      );
      const tripLegs = [...responseTripLegs, ...missingExistingTripLegs].map(
        (tripLeg) => {
          if (
            tripLeg.tripLegReferenceID !== action.payload.tripLegReferenceID
          ) {
            return tripLeg;
          }

          const status = tripLeg.status?.toLowerCase();
          const isCanceledLate = status === tripLegStatusCanceledLate;
          const canceledStatus = isCanceledLate
            ? tripLegStatusCanceledLate
            : tripLegStatusCanceled;

          return {
            ...tripLeg,
            status: canceledStatus,
            tripLegStatusBC: isCanceledLate
              ? tripLegStatusBCCanceledLate
              : tripLegStatusBCCanceled,
          };
        },
      );

      reservations.splice(idx, 1, {
        ...action.payload.reservation,
        tripLegs: sortTripLegsBySequence(tripLegs),
      });
    },
    removeReservation: (
      state: PassengerState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      const reservations = state.currentPassengerFutureReservations;
      const idx = reservations.findIndex(
        (r) => r.reservationReferenceID === action.payload,
      );
      if (idx < 0) return;
      reservations.splice(idx, 1);
    },
    // Optimistic update for a full standing-order cancel: the cancel API only
    // marks the STO inactive (occurrences are cancelled asynchronously by the
    // backend handler), so the rows are removed locally and the WebSocket-
    // driven refetch reconciles with server truth afterwards. See
    // docs/sto-list-refresh-plan.md.
    removeStandingOrderReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      state.currentPassengerFutureReservations =
        state.currentPassengerFutureReservations.filter(
          (reservation) => !belongsToStandingOrder(reservation, action.payload),
        );
      // Suppress this STO in refetched pages until the server confirms the
      // cancel cascade finished (see confirmCompletedStandingOrderCancels and
      // confirmStandingOrderCancelCompleted).
      state.pendingStandingOrderCancels[action.payload] = true;
    },
    // Deterministic confirmation from the backend's StandingOrderCancelled
    // WebSocket marker (end of the cancel cascade): clears the suppression
    // even when the passenger's future list spans multiple pages, where the
    // zero-occurrence page heuristic above can never fire.
    confirmStandingOrderCancelCompleted: (
      state: PassengerState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      delete state.pendingStandingOrderCancels[action.payload];
    },
    replaceTripLegs: (
      state: PassengerState,
      action: {
        type: string;
        payload: { reservationReferenceID: string; legOrder: TripLeg[] };
      },
    ) => {
      const reservation = state.currentPassengerFutureReservations.find(
        (r) =>
          r.reservationReferenceID === action.payload.reservationReferenceID,
      );
      if (reservation) {
        reservation.tripLegs = action.payload.legOrder;
      }
    },
    setSavingPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingPassenger = action.payload;
    },
    setSavedPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedPassenger = action.payload;
    },
    updateTripLegInReservation: (
      state: PassengerState,
      action: {
        type: string;
        payload: {
          reservationReferenceID: string;
          tripLegReferenceID: string;
          transportationProviderID: number;
          status: string;
        };
      },
    ) => {
      const reservationIndex =
        state.currentPassengerFutureReservations.findIndex(
          (r) =>
            r.reservationReferenceID === action.payload.reservationReferenceID,
        );
      if (reservationIndex >= 0) {
        const reservation =
          state.currentPassengerFutureReservations[reservationIndex];
        const tripLegIndex = reservation.tripLegs.findIndex(
          (tl: TripLeg) =>
            tl.tripLegReferenceID === action.payload.tripLegReferenceID,
        );
        if (tripLegIndex >= 0) {
          const tripLeg = {
            ...reservation.tripLegs[tripLegIndex],
            status: action.payload.status,
            transportationProviderID: action.payload.transportationProviderID,
          };
          const tripLegs = [...reservation.tripLegs];
          tripLegs.splice(tripLegIndex, 1, tripLeg);
          const updatedReservation = { ...reservation, tripLegs };
          const reservations = [...state.currentPassengerFutureReservations];
          reservations.splice(reservationIndex, 1, updatedReservation);
          state.currentPassengerFutureReservations = reservations;
        }
      }
    },
    setPassengerEventEmitted: (
      state: PassengerState,
      action: { type: string; payload: Passenger | null },
    ) => {
      state.passengerEventEmitted = action.payload;
    },
    clearCurrentPassengerReservations: (state: PassengerState) => {
      state.currentPassengerFutureReservations = [];
      state.pendingStandingOrderCancels = {};
      state.currentPassengerFutureReservationsNextPageData = undefined;
      state.loadingCurrentPassengerFutureReservations = false;
      state.loadedCurrentPassengerFutureReservations = false;
      state.currentPassengerPastReservations = [];
      state.currentPassengerPastReservationsNextPageData = undefined;
      state.loadingCurrentPassengerPastReservations = false;
      state.loadedCurrentPassengerPastReservations = false;
    },
  },
});

export const { setPassengerSearchClientBC } = slice.actions;
export const { setPassengerSearchText } = slice.actions;
export const { setPassengerSearchPage } = slice.actions;
export const { setPassengerSearchPageSize } = slice.actions;
export const { setPassengers } = slice.actions;
export const { clearPassengers } = slice.actions;
export const { setLoadingPassengers } = slice.actions;
export const { setLoadedPassengers } = slice.actions;
export const { setCurrentPassenger } = slice.actions;
export const { setLoadingCurrentPassenger } = slice.actions;
export const { setLoadedCurrentPassenger } = slice.actions;
export const { setCurrentPassengerPreferences } = slice.actions;
export const { setLoadingCurrentPassengerPreferences } = slice.actions;
export const { setLoadedCurrentPassengerPreferences } = slice.actions;
export const { setSavingCurrentPassengerPreferences } = slice.actions;
export const { setSavedCurrentPassengerPreferences } = slice.actions;
export const { setCurrentPassengerPastReservations } = slice.actions;
export const { appendCurrentPassengerPastReservations } = slice.actions;
export const { setLoadingCurrentPassengerPastReservations } = slice.actions;
export const { setLoadedCurrentPassengerPastReservations } = slice.actions;
export const { setCurrentPassengerPastReservationsNextPageData } =
  slice.actions;
export const { setCurrentPassengerFutureReservations } = slice.actions;
export const { appendCurrentPassengerFutureReservations } = slice.actions;
export const { setLoadingCurrentPassengerFutureReservations } = slice.actions;
export const { setLoadedCurrentPassengerFutureReservations } = slice.actions;
export const { setCurrentPassengerFutureReservationsNextPageData } =
  slice.actions;
export const { setCurrentPassengerReservationLookup } = slice.actions;
export const { setLoadingCurrentPassengerReservationLookup } = slice.actions;
export const { setLoadedCurrentPassengerReservationLookup } = slice.actions;
export const { clearCurrentPassengerReservationLookup } = slice.actions;
export const { mergeTripLegIntoReservationLookup } = slice.actions;
export const { addFuturePassengerReservation } = slice.actions;
export const { addPastPassengerReservation } = slice.actions;
export const { addFutureTripLeg } = slice.actions;
export const { updateFutureTripLegs } = slice.actions;
export const { replaceReservation } = slice.actions;
export const { replaceReservationAfterCancelTripLeg } = slice.actions;
export const { removeReservation } = slice.actions;
export const { removeStandingOrderReservations } = slice.actions;
export const { confirmStandingOrderCancelCompleted } = slice.actions;
export const { replaceTripLegs } = slice.actions;
export const { setSavingPassenger } = slice.actions;
export const { setSavedPassenger } = slice.actions;
export const { updateTripLegInReservation } = slice.actions;
export const { setPassengerEventEmitted } = slice.actions;
export const { clearCurrentPassengerReservations } = slice.actions;

export const fetchAllPassengersAsync =
  (page: number, pageSize: number) => (dispatch: Function) => {
    const passengers = {
      itemCount: 0,
      currentPage: 1,
      totalPages: 0,
      items: [],
    };
    dispatch(setPassengers(passengers));
    dispatch(setLoadedPassengers(false));
    passengerService.getAllPassengers(page, pageSize).subscribe({
      next: (passengers) => {
        dispatch(setPassengers(passengers));
        dispatch(setLoadingPassengers(false));
        dispatch(setLoadedPassengers(true));
      },
      error: (err) => {
        dispatch(setLoadingPassengers(false));
        dispatch(setLoadedPassengers(false));
      },
    });
  };

export const fetchPassengersAsync =
  (clientBC: string, searchText: string, page: number, pageSize: number) =>
  (dispatch: Function) => {
    dispatch(setLoadingPassengers(true));
    dispatch(setLoadedPassengers(false));
    passengerService
      .getPassengers(clientBC, searchText, page, pageSize)
      .subscribe({
        next: (passengers) => {
          dispatch(setPassengers(passengers));
          dispatch(setLoadingPassengers(false));
          dispatch(setLoadedPassengers(true));
        },
        error: (err) => {
          dispatch(setLoadingPassengers(false));
          dispatch(setLoadedPassengers(false));
        },
      });
  };

export const fetchPassengerAsync =
  (id: string, signal?: AbortSignal) => (dispatch: Function) => {
    dispatch(setLoadingCurrentPassenger(true));
    dispatch(setLoadedCurrentPassenger(false));
    dispatch(setCurrentPassenger(null));
    passengerService.getPassenger(id, signal).subscribe({
      next: (passenger) => {
        dispatch(setCurrentPassenger(passenger));
        dispatch(setLoadingCurrentPassenger(false));
        dispatch(setLoadedCurrentPassenger(true));
      },
      error: (err) => {
        dispatch(setLoadingCurrentPassenger(false));
        dispatch(setLoadedCurrentPassenger(false));
      },
    });
  };

export const fetchPassengerPreferencesAsync =
  (id: string, clientBC?: string) => (dispatch: Function) => {
    dispatch(setLoadingCurrentPassengerPreferences(true));
    dispatch(setLoadedCurrentPassengerPreferences(false));
    dispatch(setCurrentPassengerPreferences([]));
    passengerService.getPassengerPreferences(id, clientBC).subscribe({
      next: (prefs) => {
        dispatch(setCurrentPassengerPreferences(prefs));
        dispatch(setLoadingCurrentPassengerPreferences(false));
        dispatch(setLoadedCurrentPassengerPreferences(true));
      },
      error: (err) => {
        dispatch(setLoadingCurrentPassengerPreferences(false));
        dispatch(setLoadedCurrentPassengerPreferences(false));
      },
    });
  };

export const savePassengerPreferencesAsync =
  (id: string, prefs: PassengerPreference[]) => (dispatch: Function) => {
    dispatch(setSavingCurrentPassengerPreferences(true));
    dispatch(setSavedCurrentPassengerPreferences(false));
    passengerService.savePassengerPreferences(id, prefs).subscribe({
      next: (updatedPrefs) => {
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.updatePassengerPreferencesSuccess',
          ),
        );
        dispatch(setCurrentPassengerPreferences(updatedPrefs));
        dispatch(setSavingCurrentPassengerPreferences(false));
        dispatch(setSavedCurrentPassengerPreferences(true));
      },
      error: (err) => {
        dispatch(setSavingCurrentPassengerPreferences(false));
        dispatch(setSavedCurrentPassengerPreferences(false));
      },
    });
  };

/**
 * Builds the page-1 fetch + load-more thunk pair for one reservation list,
 * sharing ONE latest-wins token per list: overlapping refetches can resolve
 * out of order (server latency > refetch gap during an STO cancel cascade),
 * and a stale response — page 1 or a late load-more page — must not clobber a
 * newer list, resurrect optimistically-cancelled STO rows after the
 * suppression cleared, or append a previous passenger's page after a switch.
 * The page-1 fetch bumps the token; load-more only checks it, so a load-more
 * never invalidates a page-1 fetch but any page-1 fetch issued after it makes
 * its response stale. One closure per list makes crossing the two lists'
 * tokens unrepresentable.
 */
const createReservationListFetchers = (actions: {
  setList: Function;
  appendList: Function;
  setNextPageData: Function;
  setLoading: Function;
  setLoaded: Function;
  selectNextPageData: (state: any) => NextPageData | undefined;
}) => {
  let fetchSeq = 0;

  /**
   * Marks every request currently in flight stale without issuing a new one.
   * Needed when something outside the fetch cycle invalidates what those
   * responses would mean — specifically the StandingOrderCancelled marker,
   * which clears a cancel suppression that an already-issued request's server
   * snapshot predates: without this, that response passes the token check and
   * merges unfiltered, putting the just-cancelled rows back.
   *
   * It restores both status flags, because the discarded response returns
   * before clearing them and nothing here schedules a replacement fetch.
   * `loaded` goes back to true only when a prior load actually succeeded —
   * nextPageData is set by every successful response and cleared with the
   * lists, so it is the record of that. Leaving `loaded` false would strand
   * the list in a never-loaded state that consumers gating on it (currently
   * ReservationDetailsPage) would try to repair with an extra fetch.
   */
  const invalidateInFlight = (dispatch: Function, getState: Function) => {
    fetchSeq++;
    dispatch(actions.setLoading(false));
    if (actions.selectNextPageData(getState())) {
      dispatch(actions.setLoaded(true));
    }
  };

  const fetchPage =
    (request: TransportReservationRequest) => (dispatch: Function) => {
      const seq = ++fetchSeq;
      dispatch(actions.setLoading(true));
      dispatch(actions.setLoaded(false));
      reservationService.getPassengerReservations(request).subscribe({
        next: (response) => {
          if (seq !== fetchSeq) return; // stale response
          dispatch(actions.setList(response));
          dispatch(actions.setNextPageData(response));
          dispatch(actions.setLoading(false));
          dispatch(actions.setLoaded(true));
        },
        error: () => {
          if (seq !== fetchSeq) return; // stale response
          dispatch(actions.setLoading(false));
          dispatch(actions.setLoaded(false));
        },
      });
    };

  const loadMore =
    (request: TransportReservationRequest) =>
    (dispatch: Function, getState: Function) => {
      const nextPageData = actions.selectNextPageData(getState());
      if (!nextPageData?.hasMorePages) return;

      const seq = fetchSeq; // check-only: see factory doc comment
      dispatch(actions.setLoading(true));
      reservationService
        .getPassengerReservations({
          ...request,
          page: nextPageData?.nextPage ?? undefined,
          pageSize: nextPageData?.pageSize,
        })
        .subscribe({
          next: (response) => {
            if (seq !== fetchSeq) return; // superseded by a newer page-1 fetch
            dispatch(actions.appendList(response));
            dispatch(actions.setNextPageData(response));
            dispatch(actions.setLoading(false));
          },
          error: () => {
            if (seq !== fetchSeq) return; // superseded by a newer page-1 fetch
            dispatch(actions.setLoading(false));
          },
        });
    };

  return { fetchPage, loadMore, invalidateInFlight };
};

const pastReservationListFetchers = createReservationListFetchers({
  setList: setCurrentPassengerPastReservations,
  appendList: appendCurrentPassengerPastReservations,
  setNextPageData: setCurrentPassengerPastReservationsNextPageData,
  setLoading: setLoadingCurrentPassengerPastReservations,
  setLoaded: setLoadedCurrentPassengerPastReservations,
  selectNextPageData: (state) =>
    state?.passenger?.currentPassengerPastReservationsNextPageData,
});

const futureReservationListFetchers = createReservationListFetchers({
  setList: setCurrentPassengerFutureReservations,
  appendList: appendCurrentPassengerFutureReservations,
  setNextPageData: setCurrentPassengerFutureReservationsNextPageData,
  setLoading: setLoadingCurrentPassengerFutureReservations,
  setLoaded: setLoadedCurrentPassengerFutureReservations,
  selectNextPageData: (state) =>
    state?.passenger?.currentPassengerFutureReservationsNextPageData,
});

export const fetchPassengerPastReservationsAsync =
  pastReservationListFetchers.fetchPage;
export const fetchPassengerFutureReservationsAsync =
  futureReservationListFetchers.fetchPage;
export const loadMorePassengerPastReservationsAsync =
  pastReservationListFetchers.loadMore;
export const loadMorePassengerFutureReservationsAsync =
  futureReservationListFetchers.loadMore;

/**
 * Applies the backend's end-of-cascade marker for one standing order: discards
 * future-list requests already in flight, then lifts that STO's suppression.
 *
 * Both steps live here so the order is not a caller's responsibility — a
 * request issued mid-cascade can be carrying a snapshot from before the
 * cascade finished, and letting one merge after the suppression lifted would
 * put the just-cancelled rows back.
 *
 * In-flight requests are only disturbed when this STO actually has a
 * suppression to protect. Markers arrive for cancels this session did not
 * initiate too, and discarding an unrelated in-flight fetch there would throw
 * away a load nothing is scheduled to retry.
 */
export const applyStandingOrderCancelMarker =
  (standingOrderUUID: string) => (dispatch: Function, getState: Function) => {
    const isSuppressed =
      getState()?.passenger?.pendingStandingOrderCancels?.[
        standingOrderUUID
      ] === true;
    if (isSuppressed) {
      futureReservationListFetchers.invalidateInFlight(dispatch, getState);
    }
    dispatch(confirmStandingOrderCancelCompleted(standingOrderUUID));
  };

export const fetchPassengerReservationByReferenceAsync =
  (
    passengerUUID: string,
    reservationReferenceID: string,
    clientBC?: string | null,
    fallbackErrorMessage?: string,
  ) =>
  async (dispatch: Function) => {
    dispatch(setLoadingCurrentPassengerReservationLookup(true));
    dispatch(setLoadedCurrentPassengerReservationLookup(false));
    try {
      const reservation = await firstValueFrom(
        reservationService.getReservationByReferenceID({
          reservationID: reservationReferenceID,
          passengerID: passengerUUID,
          ...(clientBC && { clientBC }),
        }),
      );
      dispatch(setCurrentPassengerReservationLookup(reservation));
      dispatch(setLoadingCurrentPassengerReservationLookup(false));
      dispatch(setLoadedCurrentPassengerReservationLookup(true));
    } catch (err: any) {
      dispatch(setLoadingCurrentPassengerReservationLookup(false));
      dispatch(setLoadedCurrentPassengerReservationLookup(false));
      dispatch(
        uiSlice.setErrorMessage(err?.message || fallbackErrorMessage || ''),
      );
    }
  };

export const fetchMissingTripLegIntoReservationLookupAsync =
  (
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegReferenceID: string,
  ) =>
  async (dispatch: Function) => {
    try {
      const tripLeg = await firstValueFrom(
        reservationService.getTripLeg(
          passengerUUID,
          reservationReferenceID,
          tripLegReferenceID,
        ),
      );
      dispatch(mergeTripLegIntoReservationLookup(tripLeg));
    } catch {
      // silently ignore — the leg may not be accessible
    }
  };

export const createPassengerAsync =
  (passenger: Passenger) => (dispatch: Function) => {
    dispatch(setSavingPassenger(true));
    dispatch(setSavedPassenger(false));
    dispatch(setCurrentPassenger(null));
    passengerService.createPassenger(passenger).subscribe({
      next: (passenger) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.createPassengerSuccess'));
        // dispatch(addPassenger(passenger));
        dispatch(setCurrentPassenger(passenger));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(false));
      },
    });
  };

export const updatePassengerAsync =
  (passenger: Passenger) => (dispatch: Function) => {
    dispatch(setSavingPassenger(true));
    dispatch(setSavedPassenger(false));
    passengerService.updatePassenger(passenger).subscribe({
      next: (passenger) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.updatePassengerSuccess'));
        dispatch(setCurrentPassenger(passenger));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(false));
      },
    });
  };

export const addPassengerReservationAddressAsync =
  (
    passengerUUID: string,
    address: PassengerAddress,
    passengerAddressUseBC: string,
    label: string,
  ) =>
  (dispatch: Function) => {
    passengerService
      .createPassengerAddress(
        passengerUUID,
        address,
        passengerAddressUseBC,
        label,
      )
      .subscribe({
        next: (passenger) => {
          dispatch(setCurrentPassenger(passenger));
          dispatch(
            uiSlice.setSuccessMessage('snackbar.addPassengerAddressSuccess'),
          );
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const deletePassengerReservationAddressAsync =
  (passengerUUID: string, label: string) => (dispatch: Function) => {
    passengerService.deletePassengerAddress(passengerUUID, label).subscribe({
      next: (passenger) => {
        dispatch(setCurrentPassenger(passenger));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deletePassengerAddressSuccess'),
        );
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const selectPassengerSearchClientBC = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchClientBC;
export const selectPassengerSearchText = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchText;
export const selectPassengerSearchPage = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchPage;
export const selectPassengerSearchPageSize = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchPageSize;
export const selectPassengers = (state: { passenger: PassengerState }) =>
  state.passenger.passengers;
export const selectLoadingPassengers = (state: { passenger: PassengerState }) =>
  state.passenger.loadingPassengers;
export const selectLoadedPassengers = (state: { passenger: PassengerState }) =>
  state.passenger.loadedPassengers;
export const selectCurrentPassenger = (state: { passenger: PassengerState }) =>
  state.passenger.currentPassenger;
export const selectLoadingCurrentPassenger = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassenger;
export const selectLoadedCurrentPassenger = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassenger;
export const selectCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerPreferences;
export const selectLoadingCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerPreferences;
export const selectLoadedCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerPreferences;
export const selectSavingCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.savingCurrentPassengerPreferences;
export const selectSavedCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.savedCurrentPassengerPreferences;
export const selectCurrentPassengerPastReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerPastReservations;
export const selectLoadingCurrentPassengerPastReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerPastReservations;
export const selectLoadedCurrentPassengerPastReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerPastReservations;
export const selectCurrentPassengerPastReservationsNextPageData = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerPastReservationsNextPageData;
export const selectCurrentPassengerFutureReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerFutureReservations;
export const selectLoadingCurrentPassengerFutureReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerFutureReservations;
export const selectLoadedCurrentPassengerFutureReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerFutureReservations;
export const selectCurrentPassengerFutureReservationsNextPageData = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerFutureReservationsNextPageData;
export const selectCurrentPassengerReservationLookup = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerReservationLookup;
export const selectLoadingCurrentPassengerReservationLookup = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerReservationLookup;
export const selectLoadedCurrentPassengerReservationLookup = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerReservationLookup;
export const selectSavingPassenger = (state: { passenger: PassengerState }) =>
  state.passenger.savingPassenger;
export const selectSavedPassenger = (state: { passenger: PassengerState }) =>
  state.passenger.savedPassenger;
export const selectPassengerEventEmitted = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerEventEmitted;

export default slice.reducer;
